import type { RecordWorkOrderDetailsInput, Workshop, WorkOrderDetailsView } from '@chukua/common';

export interface WorkshopFormValues {
  workshop?: Workshop;
  garageId?: string;
  odometerKm: string;
  symptoms: string;
  diagnosis: string;
  warrantyMonths: string;
}

export const WORKSHOP_LABELS: Record<Workshop, string> = { in_house: 'In-house', garage: 'External garage' };

const BLANK_WORKSHOP_FORM: WorkshopFormValues = { odometerKm: '', symptoms: '', diagnosis: '', warrantyMonths: '' };

function asText(value: number | null): string {
  return value === null ? '' : String(value);
}

export function workshopFormFrom(details: WorkOrderDetailsView | null): WorkshopFormValues {
  if (!details) return BLANK_WORKSHOP_FORM;
  return {
    workshop: details.workshop,
    garageId: details.garageId ?? undefined,
    odometerKm: asText(details.odometerKm),
    symptoms: details.symptoms ?? '',
    diagnosis: details.diagnosis ?? '',
    warrantyMonths: asText(details.warrantyMonths),
  };
}

/** Whether the workshop can still be changed: a closed or cancelled order keeps the record it ended with. */
export function canEditWorkshop(orderState: string, canManage: boolean): boolean {
  return canManage && orderState !== 'verified_closed' && orderState !== 'cancelled';
}

function wholeNumber(typed: string): number | null | 'invalid' {
  const trimmed = typed.replace(/[,\s]/g, '');
  if (trimmed === '') return null;
  return /^\d+$/.test(trimmed) ? Number(trimmed) : 'invalid';
}

function present<K extends string, V>(key: K, value: V | null | undefined): Partial<Record<K, V>> {
  if (value === null || value === undefined || value === '') return {};
  return { [key]: value } as Record<K, V>;
}

/**
 * The request body for `POST /work-orders/:id/details`. The garage and the warranty belong to external work only, so switching
 * to in-house drops them rather than sending a combination the API refuses.
 */
export function toWorkshopInput(values: WorkshopFormValues): { ok: true; input: RecordWorkOrderDetailsInput } | { ok: false; reason: string } {
  if (!values.workshop) return { ok: false, reason: 'Choose where the work is done' };
  const isGarage = values.workshop === 'garage';
  if (isGarage && !values.garageId) return { ok: false, reason: 'Choose the garage' };
  const odometerKm = wholeNumber(values.odometerKm);
  const warrantyMonths = isGarage ? wholeNumber(values.warrantyMonths) : null;
  if (odometerKm === 'invalid') return { ok: false, reason: 'The odometer is a whole number of kilometres' };
  if (warrantyMonths === 'invalid') return { ok: false, reason: 'The warranty is a whole number of months' };
  return {
    ok: true,
    input: {
      workshop: values.workshop,
      ...present('garageId', isGarage ? values.garageId : null),
      ...present('odometerKm', odometerKm),
      ...present('symptoms', values.symptoms.trim()),
      ...present('diagnosis', values.diagnosis.trim()),
      ...present('warrantyMonths', warrantyMonths),
    },
  };
}
